/**
 * IonSidebar — recent conversations plus "New chat", on the left of the Ion full
 * page (A2).
 *
 * Only under `/assistant`, and only in the `full` geometry. That is BA's
 * decision and it is also the only place it fits: the Explorer already has Ion
 * docked on the right and the filter panel on the left, so a third rail there
 * would be a third thing competing for the same screen. The top navigation is
 * untouched.
 *
 * Recents move here rather than being duplicated — `IonHome` used to render its
 * own "Recent conversations" block at the bottom of the landing page. Two lists
 * of the same thing on one screen is the kind of clutter A1 exists to remove,
 * and the sidebar is where every assistant with this layout puts them: always
 * reachable, including MID-conversation, which the old placement never was (it
 * lived on the empty landing and vanished the moment you asked something).
 *
 * Uses the same `listConversations` the landing block used and the history
 * drawer uses — no second fetch path. Rows NAVIGATE (`/assistant/<id>`) rather
 * than loading the conversation themselves: the route owns which conversation is
 * open, so a click here produces the same state as a reload or a pasted link.
 */
import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'

import { listConversations } from '../../services/ionConversationService.js'
import { currentConversationId } from '../../state/chatStore.js'
import { IconPlus } from '../shared/Icons.jsx'

/** "3h", "2d" — compact, because the row's job is the title. */
function relativeShort(iso) {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d`
  return `${Math.round(days / 7)}w`
}

export function IonSidebar() {
  const [, navigate] = useLocation()
  const [items, setItems] = useState([])
  const activeId = currentConversationId.value

  // Re-fetch when the active conversation changes: starting a new chat or
  // opening an old one both change what the list should show as current, and a
  // turn in a brand-new conversation is what puts it in the list at all.
  useEffect(() => {
    let cancelled = false
    listConversations({ limit: 20 })
      .then((data) => {
        if (!cancelled) setItems(data.conversations || [])
      })
      .catch(() => {
        /* Silent: recents are a convenience. An error card here would be louder
           than the feature is important, and the chat itself still works. */
      })
    return () => {
      cancelled = true
    }
  }, [activeId])

  return (
    <aside class="ion-sidebar" aria-label="Conversations">
      {/* Navigates rather than clearing the store directly: `/assistant` IS
          "a new chat" now (AssistantPage owns that rule), so going there is the
          only way to say it — and it leaves the address bar honest. */}
      <button
        type="button"
        class="ion-sidebar__new"
        onClick={() => navigate('/assistant')}
      >
        <IconPlus size={15} />
        <span>New chat</span>
      </button>

      {items.length > 0 && (
        <>
          <h2 class="ion-sidebar__heading">Recents</h2>
          <ul class="ion-sidebar__list">
            {items.map((item) => (
              <li key={item.conversationId}>
                <button
                  type="button"
                  class={`ion-sidebar__row${
                    item.conversationId === activeId ? ' ion-sidebar__row--active' : ''
                  }`}
                  onClick={() => navigate(`/assistant/${item.conversationId}`)}
                  title={item.title || 'Untitled conversation'}
                >
                  <span class="ion-sidebar__row-title">
                    {item.title || 'Untitled conversation'}
                  </span>
                  <span class="ion-sidebar__row-time">
                    {relativeShort(item.lastActivityAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  )
}
